import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useHubSystemInfo from './useHubSystemInfo';
import { selectHubApiKeyModalVisible, selectWelcomeMessagePopupVisible, showHubApiKeyModal } from '@src/features/interface/interfaceSlice';
import { selectSessionProperties, selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import useKemuHubLink from './useHubLink';

/**
 * Hook to manage the Hub API Key modal visibility
 * @returns Object with modal state and control functions
 */
const useHubApiKeyModal = () => {
  const dispatch = useDispatch();
  const systemInfo = useHubSystemInfo();
  const reduxVisible = useSelector(selectHubApiKeyModalVisible);
  const sessionProperties = useSelector(selectSessionProperties);
  const [isDismissed, setIsDismissed] = useState(false);
  const userInfo = useSelector(selectSignedUserProfile)
  const welcomeMessagePopupVisible = useSelector(selectWelcomeMessagePopupVisible);
  const {online: isHubOnline} = useKemuHubLink();
  const [hubWasOnline, setHubWasOnline] = useState(isHubOnline);
  
  const firstTimeUser = Boolean(userInfo && !sessionProperties.watchedVideos.welcomeMessage);
  const onboardingVisible = sessionProperties.onboardingModalVisible || (firstTimeUser && !hubWasOnline);
  const hasValidApiKey = systemInfo?.hasValidApiKeyConfigured ?? true;
  const otherModalsVisible = welcomeMessagePopupVisible;

  // Reset dismissed state when API key becomes valid
  useEffect(() => {
    if (hasValidApiKey) {
      setIsDismissed(false);
      dispatch(showHubApiKeyModal(false));
    }
  }, [hasValidApiKey, dispatch]);

  // Auto-show modal when API key is invalid and not dismissed
  useEffect(() => {
    if (!hasValidApiKey && !isDismissed && !reduxVisible) {
      dispatch(showHubApiKeyModal(true));
    }
  }, [hasValidApiKey, isDismissed, reduxVisible, dispatch]);

  const shouldShow = useMemo(() => {
    return reduxVisible && !hasValidApiKey && !onboardingVisible && hubWasOnline && !otherModalsVisible;
  }, [reduxVisible, hasValidApiKey, onboardingVisible, hubWasOnline, otherModalsVisible]);

  const dismiss = () => {
    setIsDismissed(true);
    dispatch(showHubApiKeyModal(false));
  };

  const show = () => {
    setIsDismissed(false);
    dispatch(showHubApiKeyModal(true));
  };

  // Keeps track of whether the hub was online at some point
  useEffect(() => {
    if(isHubOnline){
      setHubWasOnline(true);
    }
  }, [isHubOnline]);

  return {
    shouldShow,
    isDismissed,
    dismiss,
    show,
    hasValidApiKey,
  };
};

export default useHubApiKeyModal;

